interface TokenData {
  token: string;
  expiresAt: number;
  refreshedAt: number;
  userEmail?: string;
  staySignedIn?: boolean;
}

export class SecureTokenManager {
  private static readonly TOKEN_KEY = 'gapi_token';
  private static readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  private static readonly USE_LOCALSTORAGE_FALLBACK = true;

  private static encrypt(data: string): string {
    // Simple base64 encoding for demo - in production use Web Crypto API
    try {
      return btoa(data);
    } catch (error) {
      return '';
    }
  }

  private static decrypt(data: string): string {
    try {
      return atob(data);
    } catch (error) {
      return '';
    }
  }

  static setToken(token: string, expiresIn: number = 3600, userEmail?: string, staySignedIn?: boolean): void {
    try {
      const tokenData: TokenData = {
        token,
        expiresAt: Date.now() + (expiresIn * 1000),
        refreshedAt: Date.now(),
        userEmail,
        staySignedIn,
      };

      const encrypted = this.encrypt(JSON.stringify(tokenData));
      if (encrypted) {
        // Use sessionStorage as primary storage
        sessionStorage.setItem(this.TOKEN_KEY, encrypted);
        
        // Use localStorage as fallback for persistence across tabs
        if (this.USE_LOCALSTORAGE_FALLBACK) {
          localStorage.setItem(this.TOKEN_KEY + '_backup', encrypted);
        }
      }
    } catch (error) {
      // Failed to set token - continue silently
    }
  }

  static getToken(): string | null {
    try {
      // Try sessionStorage first
      let encrypted = sessionStorage.getItem(this.TOKEN_KEY);
      
      // Fallback to localStorage if not in sessionStorage
      if (!encrypted && this.USE_LOCALSTORAGE_FALLBACK) {
        encrypted = localStorage.getItem(this.TOKEN_KEY + '_backup');
        
        // If found in localStorage, restore to sessionStorage
        if (encrypted) {
          sessionStorage.setItem(this.TOKEN_KEY, encrypted);
        }
      }
      
      if (!encrypted) return null;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return null;

      const tokenData: TokenData = JSON.parse(decrypted);

      // Check if token is expired
      if (Date.now() >= tokenData.expiresAt) {
        this.clearToken();
        return null;
      }

      return tokenData.token;
    } catch (error) {
      this.clearToken();
      return null;
    }
  }

  static shouldRefresh(): boolean {
    try {
      const encrypted = sessionStorage.getItem(this.TOKEN_KEY);
      if (!encrypted) return false;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return false;

      const tokenData: TokenData = JSON.parse(decrypted);

      return (tokenData.expiresAt - Date.now()) < this.REFRESH_THRESHOLD;
    } catch (error) {
      return false;
    }
  }

  static clearToken(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      // Clear backup token
      if (this.USE_LOCALSTORAGE_FALLBACK) {
        localStorage.removeItem(this.TOKEN_KEY + '_backup');
      }
      // Also clear legacy localStorage tokens
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('google_credential');
    } catch (error) {
      // Failed to clear token - continue silently
    }
  }

  static isTokenValid(): boolean {
    return this.getToken() !== null;
  }

  static getTokenExpiry(): number | null {
    try {
      const encrypted = sessionStorage.getItem(this.TOKEN_KEY);
      if (!encrypted) return null;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return null;

      const tokenData: TokenData = JSON.parse(decrypted);
      return tokenData.expiresAt;
    } catch (error) {
      return null;
    }
  }

  static getUserEmail(): string | null {
    try {
      const encrypted = sessionStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY + '_backup');
      if (!encrypted) return null;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return null;

      const tokenData: TokenData = JSON.parse(decrypted);
      return tokenData.userEmail || null;
    } catch (error) {
      return null;
    }
  }

  static getStaySignedIn(): boolean {
    try {
      const encrypted = sessionStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY + '_backup');
      if (!encrypted) return false;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return false;

      const tokenData: TokenData = JSON.parse(decrypted);
      return tokenData.staySignedIn || false;
    } catch (error) {
      return false;
    }
  }

  static updateTokenData(updates: Partial<TokenData>): void {
    try {
      const encrypted = sessionStorage.getItem(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY + '_backup');
      if (!encrypted) return;

      const decrypted = this.decrypt(encrypted);
      if (!decrypted) return;

      const tokenData: TokenData = JSON.parse(decrypted);
      const updatedData = { ...tokenData, ...updates };
      
      const newEncrypted = this.encrypt(JSON.stringify(updatedData));
      if (newEncrypted) {
        sessionStorage.setItem(this.TOKEN_KEY, newEncrypted);
        if (this.USE_LOCALSTORAGE_FALLBACK) {
          localStorage.setItem(this.TOKEN_KEY + '_backup', newEncrypted);
        }
      }
    } catch (error) {
      // Failed to update token - continue silently
    }
  }
}